<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import MapHeader from '@/components/MapHeader.vue'
import RoutePageHeader from '@/components/RoutePageHeader.vue'
import StaticPageArticle from '@/components/static/StaticPageArticle.vue'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useSafeAreaHeaderClearance } from '@/services/safeAreaService'
import { staticPageService, type StaticPage } from '@/services/staticPageService'

interface Props {
  pageId: string
}

const props = defineProps<Props>()
const pageTopPadding = useSafeAreaHeaderClearance(48)

const page = ref<StaticPage | null>(null)
const isLoading = ref(false)
const loadError = ref<string | null>(null)

const pageTitle = computed(() => page.value?.title ?? 'Page')

const loadPage = async () => {
  isLoading.value = true
  loadError.value = null

  try {
    page.value = await staticPageService.getOrLoadPage(props.pageId)
    if (!page.value) {
      loadError.value = 'Page not found.'
    }
  } catch (error) {
    loadError.value = 'Failed to load page.'
    logService.error(LOG_CATEGORIES.UI, 'Failed to load static page', {
      error,
      pageId: props.pageId,
    })
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.pageId,
  () => {
    void loadPage()
  },
)

onMounted(() => {
  void loadPage()
})
</script>

<template>
  <div class="h-screen overflow-hidden bg-[#FEFEFE]">
    <MapHeader />
    <main :style="{ paddingTop: pageTopPadding }" class="h-full overflow-y-auto">
      <RoutePageHeader :title="pageTitle" />

      <StaticPageArticle :html-content="page?.htmlContent ?? ''" :is-loading="isLoading" />

      <div v-if="loadError" class="px-5 pb-12 sm:px-8">
        <div class="mx-auto max-w-5xl">
          <div class="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {{ loadError }}
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
